import React, { useRef } from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { selectColor } from '../../core';

const ModalBackground = styled.div`
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  z-index: 998;
  background: rgba(0, 0, 0, 0.5);
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  position: fixed;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  &.modalShow {
    animation: fadeIn 0.3s;
  }
  &.modalHidden {
    display: none;
  }
`;

const ModalContainer = styled.div`
  z-index: 999;
  background: ${selectColor('white')};
  width: 300px;
  height: auto;
  border-radius: 4px;
  padding: 16px;
  margin: 16px;
  display: flex;
  flex-direction: column;
`;

const Title = styled.h4`
  white-space: normal;
  font-size: 1.8rem;
  font-weight: normal;
  padding: 8px 0;
`;

const Subtitle = styled.p`
  white-space: normal;
  font-size: 1.2rem;
  color: ${selectColor('gray50')};
  padding: 8px 0;
`;

const BtnWrapper = styled.div`
  text-align: right;
  margin-top: 15px;
  button,
  a {
    padding: 8px 18px;
    border-radius: 4px;
  }
`;
const btnStyles = css`
  color: ${selectColor('blue50')};
  &.styledBtn {
    background: ${selectColor('blue50')};
    font-weight: 600;
    color: ${selectColor('white')};
  }
`;

const BtnCancel = styled.button`
  color: ${selectColor('blue50')};
`;

const BtnClose = styled.button`
  ${btnStyles}
`;

const LinkedBtn = styled(Link)`
  ${btnStyles}
  text-decoration: none;
`;

const LinkedAnchor = styled.a`
  ${btnStyles}
  text-decoration: none;
`;

const OptionsContainer = styled.ul`
  color: ${selectColor('gray50')};
  padding: 13px 0px 5px;
  white-space: normal;
  margin-bottom: 16px;
  &.addLines {
    border-top: 1px solid ${selectColor('gray25')};
    border-bottom: 1px solid ${selectColor('gray25')};
    max-height: 140px;
    overflow-y: scroll;
  }
  li {
    padding: 5px 0;
    display: flex;
    input {
      margin-right: 16px;
      margin-top: 4px;
    }
  }
`;

function ModalCreditsOptions({
  onClickAction,
  onClickCancel,
  onClickSelect,
  onClickFreemium,
  onClickOutside,
  defaultPlan,
  ctaFreemium,
  plans,
  ctaLink,
  ctaAnchor,
  urlLink,
  show = true,
  title = '',
  subTitle = '',
  ctaCancel = '',
  ctaOk = ''
}) {
  const showHideModal = show ? 'modalShow' : 'modalHidden';
  const outsideRef = useRef(null);
  const handleOutsideClick = e => {
    if (!outsideRef.current.contains(e.target)) onClickOutside();
  };
  return (
    <>
      <ModalBackground className={showHideModal} onClick={handleOutsideClick}>
        <ModalContainer ref={outsideRef}>
          <Title>{title}</Title>
          {subTitle && <Subtitle>{subTitle}</Subtitle>}
          {plans && (
            <OptionsContainer className={`contentBox ${plans.length > 3 ? 'addLines' : ''}`}>
              {Object.values(plans).map(
                plan =>
                  plan.availableItems > 0 && (
                    <li key={plan.publicationPlanId}>
                      <input
                        defaultChecked={plan.publicationPlanId === defaultPlan}
                        type='radio'
                        id={plan.publicationPlanId}
                        name='plans'
                        value={plan.publicationPlanId}
                        onClick={() => onClickSelect(plan.publicationPlanId, plan.name)}
                      />
                      <label htmlFor={plan.publicationPlanId}>
                        {plan.name} {plan.availableItems >= 999000 ? ` (Ilim.)` : ` (${plan.availableItems})`}
                      </label>
                    </li>
                  )
              )}
            </OptionsContainer>
          )}
          <BtnWrapper>
            {ctaCancel && <BtnCancel onClick={onClickCancel}>{ctaCancel}</BtnCancel>}
            {ctaFreemium && <BtnCancel onClick={onClickFreemium}>{ctaFreemium}</BtnCancel>}
            {ctaOk && (
              <BtnClose className={ctaCancel || ctaFreemium ? 'styledBtn' : ''} onClick={onClickAction}>
                {ctaOk}
              </BtnClose>
            )}
            {ctaLink && (
              <LinkedBtn className={ctaCancel || ctaFreemium ? 'styledBtn' : ''} onClick={onClickCancel} to={urlLink}>
                {ctaLink}
              </LinkedBtn>
            )}
            {ctaAnchor && (
              <LinkedAnchor
                className={ctaCancel || ctaFreemium ? 'styledBtn' : ''}
                onClick={onClickCancel}
                href={urlLink}
              >
                {ctaAnchor}
              </LinkedAnchor>
            )}
          </BtnWrapper>
        </ModalContainer>
      </ModalBackground>
    </>
  );
}

ModalCreditsOptions.propTypes = {
  onClickAction: PropTypes.func.isRequired
};

export { ModalCreditsOptions };
